import React, { useEffect, useState } from "react";
import styled from "styled-components";
import { DerivedForm } from "../../grammar/declension";
import { loadSutras, sutraText, sutraUrl } from "../../grammar/sutras";

/**
 * The prakriyā: each sūtra that applied, what the word looked like after it, and
 * the sūtra's own words, which are short enough to serve as the explanation. The
 * rule number links to a full commentary.
 *
 * Shared by the declension and conjugation views — a derivation is a derivation
 * whether the ending is a *sup* or a *tiṅ*.
 */
export const Prakriya = (props: { form: DerivedForm; onClose: () => void }) => {
    const [, setReady] = useState(0);

    // ~145 KB of sūtra text, fetched only once a derivation is actually opened.
    useEffect(() => {
        let cancelled = false;
        loadSutras().then(() => { if (!cancelled) setReady(n => n + 1); });
        return () => { cancelled = true; };
    }, []);

    return (
        <StyledPrakriya>
            <PrakriyaHead>
                <strong>{props.form.text}</strong>
                <PrakriyaClose type="button" onClick={props.onClose} aria-label="close">
                    &times;
                </PrakriyaClose>
            </PrakriyaHead>
            <table>
                <tbody>
                    {props.form.steps.map((s, i) => {
                        const url = sutraUrl(s.code);
                        const text = sutraText(s.code);
                        return (
                            <tr key={i}>
                                <RuleCode>
                                    {url ? (
                                        <RuleLink
                                            href={url}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            title="Read this rule on ashtadhyayi.com"
                                        >
                                            {s.code}
                                        </RuleLink>
                                    ) : s.code}
                                </RuleCode>
                                <SutraText title={text}>{text}</SutraText>
                                <td>{s.terms.join(" + ")}</td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </StyledPrakriya>
    );
};

/** A clickable form; selecting it reveals the derivation. */
export const FormButton = styled.button`
    background: none;
    border: none;
    padding: 0;
    font: inherit;
    color: inherit;
    cursor: pointer;
    border-bottom: 1px dotted transparent;

    &:hover { border-bottom-color: currentColor; }
    &[data-selected="true"] {
        font-weight: bold;
        border-bottom-color: currentColor;
    }
`;

export const Variant = styled.span`
    opacity: 0.4;
`;

const StyledPrakriya = styled.div`
  font-size: 0.65em;
  text-align: left;
  margin: 0 10px 15px 10px;
  padding: 6px 8px;
  background: ${(props) => props.theme.colors.lightgray};

  table { width: 100%; margin: 0 !important; }
  td { padding: 1px 4px; }
`;

const PrakriyaHead = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 4px;
`;

const PrakriyaClose = styled.button`
    background: none;
    border: none;
    cursor: pointer;
    font-size: 1.4em;
    line-height: 1;
    padding: 0 2px;
`;

const RuleCode = styled.td`
    white-space: nowrap;
    opacity: 0.65;
    width: 1%;
`;

const RuleLink = styled.a`
    color: inherit;
    text-decoration: none;
    border-bottom: 1px dotted currentColor;
    &:hover { opacity: 1; }
`;

/*
 * The sūtra itself, as the label for the step. A few are very long — 4.1.2 is
 * the entire list of sup affixes — so they are clipped to keep the derivation
 * readable, with the full text on hover.
 */
const SutraText = styled.td`
    font-style: italic;
    opacity: 0.8;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    max-width: 20ch;
`;
